<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { emitter } from 'shared/helpers/mitt';
import ChatAPI from 'custom/api/chat';
import { useLocoI18n } from 'custom/composables/useLocoI18n';
import { CHAT_EVENTS } from './useChat';
import ChatMessage from './ChatMessage.vue';
import ChatComposer from './ChatComposer.vue';

const props = defineProps({
  channelId: { type: Number, required: true },
  messageId: { type: Number, required: true },
  people: { type: Array, default: () => [] },
  currentUserId: { type: Number, default: null },
});

const emit = defineEmits(['close']);

const { t } = useLocoI18n();
const parent = ref(null);
const replies = ref([]);
const listRef = ref(null);

const scrollToEnd = () => {
  setTimeout(() => {
    if (listRef.value) listRef.value.scrollTop = listRef.value.scrollHeight;
  });
};

const load = async () => {
  const { data } = await ChatAPI.replies(props.channelId, props.messageId);
  parent.value = data.parent;
  replies.value = data.payload;
  scrollToEnd();
};

const upsert = message => {
  if (message.id === props.messageId) {
    parent.value = message;
    return;
  }
  if (message.parent_id !== props.messageId) return;
  const index = replies.value.findIndex(item => item.id === message.id);
  if (index === -1) {
    replies.value.push(message);
    scrollToEnd();
  } else {
    replies.value.splice(index, 1, message);
  }
};

watch(() => props.messageId, load);
onMounted(() => {
  load();
  emitter.on(CHAT_EVENTS.MESSAGE_CREATED, upsert);
  emitter.on(CHAT_EVENTS.MESSAGE_UPDATED, upsert);
});
onBeforeUnmount(() => {
  emitter.off(CHAT_EVENTS.MESSAGE_CREATED, upsert);
  emitter.off(CHAT_EVENTS.MESSAGE_UPDATED, upsert);
});
</script>

<template>
  <aside class="flex flex-col w-96 flex-shrink-0 border-l border-n-weak h-full">
    <header
      class="flex items-center justify-between px-4 h-14 border-b border-n-weak"
    >
      <span class="text-heading-3 text-n-slate-12">{{
        t('LOCO.CHAT.THREAD')
      }}</span>
      <button
        type="button"
        class="size-7 rounded-md flex items-center justify-center text-n-slate-11 hover:bg-n-alpha-2"
        :title="t('LOCO.CHAT.CLOSE')"
        @click="emit('close')"
      >
        <Icon icon="i-lucide-x" class="size-4" />
      </button>
    </header>
    <div ref="listRef" class="flex-1 overflow-y-auto py-2">
      <ChatMessage
        v-if="parent"
        :message="parent"
        :people="people"
        :current-user-id="currentUserId"
        in-thread
        @updated="upsert"
      />
      <div v-if="replies.length" class="mx-4 my-2 border-t border-n-weak" />
      <ChatMessage
        v-for="reply in replies"
        :key="reply.id"
        :message="reply"
        :people="people"
        :current-user-id="currentUserId"
        in-thread
        @updated="upsert"
      />
    </div>
    <ChatComposer
      :channel-id="channelId"
      :parent-id="messageId"
      :people="people"
      :placeholder="t('LOCO.CHAT.REPLY_PLACEHOLDER')"
      @sent="upsert"
    />
  </aside>
</template>
